import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { ArtistPortalCardState, artistPortalCardState, formatPortalFreshness } from './artistPortalCardState';
import { catalogServiceCardCopy } from './catalogServiceCards';
import { useConfirmWhileBusy } from '../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { ConnectionDisconnectModal } from './ConnectionDisconnectModal';
import styles from './ConnectionCardPortal.module.scss';

interface ConnectionCardPortalProps {
  connection: ServiceConnection;
  isBusy: boolean;
  /** Fixed by the screenshot build so two runs draw the same freshness line. */
  now?: number;
  onConnect: (integrationType: string) => void;
  onSync: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
}

/**
 * The signed-in half of a connection card: who is connected, how fresh the
 * numbers are, and the three things to do about it.
 */
const ConnectionCardPortalComponent: React.FC<ConnectionCardPortalProps> = ({
  connection,
  isBusy,
  now,
  onConnect,
  onSync,
  onDisconnect,
}) => {
  const intl = useIntl();
  const { integrationType } = connection;
  const state = artistPortalCardState(connection);

  // Read at render, never frozen: "synced 2 hours ago" must not stay at two.
  const readAt = now ?? Date.now();
  const freshness = useMemo(() => formatPortalFreshness(intl, connection, readAt), [connection, intl, readAt]);

  const copy = catalogServiceCardCopy[integrationType];
  // One YouTube sign-in for Streaming and Social: its Disconnect says that both stop.
  const isYouTube = integrationType === MusicAPIIntegrationID.YoutubeMusic;
  const serviceName = copy
    ? intl.formatMessage(copy.programName)
    : (musicServicesByMusicAPIID[integrationType]?.name ?? integrationType);

  const connect = useCallback(() => onConnect(integrationType), [integrationType, onConnect]);
  const sync = useCallback(() => onSync(integrationType), [integrationType, onSync]);
  const disconnect = useCallback(() => onDisconnect(integrationType), [integrationType, onDisconnect]);
  // The confirm stays open with a spinner until the disconnect ends (design dialogs a4).
  const disconnectDialog = useConfirmWhileBusy({ isBusy, onConfirm: disconnect });
  const askToDisconnect = disconnectDialog.open;

  if (state === ArtistPortalCardState.none) {
    return null;
  }

  if (state === ArtistPortalCardState.expired) {
    return (
      <div className={styles.connectionPortal}>
        <div className={styles.connectionAlertNote}>
          {intl.formatMessage({
            id: 'app.sml.catalog.connection.expired',
            defaultMessage: 'The service signed us out. Reconnect to keep your numbers up to date.',
          })}
        </div>
        <SMLButton variant="primary" onClick={connect} className={styles.smallButton} disabled={isBusy}>
          {intl.formatMessage({ id: 'app.sml.catalog.connection.reconnect', defaultMessage: 'Reconnect' })}
        </SMLButton>
      </div>
    );
  }

  if (state === ArtistPortalCardState.needsArtist) {
    return (
      <div className={styles.connectionPortal}>
        <div className={styles.connectionSoftNote}>
          {intl.formatMessage({
            id: 'app.sml.catalog.connection.needs_artist',
            defaultMessage: 'This sign-in holds more than one artist. Choose which artist to connect.',
          })}
        </div>
        <div className={styles.connectionActions}>
          <SMLButton variant="primary" onClick={connect} className={styles.smallButton} disabled={isBusy}>
            {intl.formatMessage({ id: 'app.sml.catalog.connection.choose_artist', defaultMessage: 'Choose artist' })}
          </SMLButton>
          {/* A login with no artist on it cannot be finished by choosing, so it must be removable. */}
          <SMLButton onClick={askToDisconnect} className={styles.smallButton} disabled={isBusy}>
            {intl.formatMessage({ id: 'app.sml.catalog.connection.disconnect', defaultMessage: 'Disconnect' })}
          </SMLButton>
        </div>
        <ConnectionDisconnectModal
          isOpen={disconnectDialog.isOpen}
          serviceName={serviceName}
          isYouTube={isYouTube}
          isBusy={disconnectDialog.isRunning}
          onCancel={disconnectDialog.cancel}
          onConfirm={disconnectDialog.confirm}
        />
      </div>
    );
  }

  return (
    <div className={styles.connectionPortal}>
      <div className={styles.connectionArtist}>
        {connection.artist?.imageUrl !== null &&
          connection.artist?.imageUrl !== undefined &&
          connection.artist.imageUrl !== '' && (
            <img src={connection.artist.imageUrl} alt="" className={styles.connectionAvatar} />
          )}
        <span>{connection.artist?.name}</span>
      </div>
      {freshness !== undefined && <div className={styles.connectionSoftNote}>{freshness}</div>}
      {connection.lastSyncError !== null && (
        <div className={styles.connectionAlertNote}>{connection.lastSyncError}</div>
      )}
      <div className={styles.connectionActions}>
        <SMLButton onClick={sync} className={styles.smallButton} disabled={isBusy}>
          {intl.formatMessage({ id: 'app.sml.catalog.connection.sync_now', defaultMessage: 'Sync now' })}
        </SMLButton>
        <SMLButton onClick={askToDisconnect} className={styles.smallButton} disabled={isBusy}>
          {intl.formatMessage({ id: 'app.sml.catalog.connection.disconnect', defaultMessage: 'Disconnect' })}
        </SMLButton>
      </div>
      <ConnectionDisconnectModal
        isOpen={disconnectDialog.isOpen}
        serviceName={serviceName}
        isYouTube={isYouTube}
        isBusy={disconnectDialog.isRunning}
        onCancel={disconnectDialog.cancel}
        onConfirm={disconnectDialog.confirm}
      />
    </div>
  );
};

export const ConnectionCardPortal = TypedMemo(ConnectionCardPortalComponent);
